import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useNoIndex } from "../../hooks/usePageSeo";
import {
  getQuestionnairePublic,
  verifyQuestionnaireIdentity,
  submitQuestionnaireResponse,
} from "../../firebase";
import DatePicker from "../../components/ui/DatePicker";
import PublicClinicHeader from "../../components/public/PublicClinicHeader";
import {
  ListBullets,
  Phone,
  IdentificationCard,
  ShieldCheck,
  Warning,
  CheckCircle,
  PaperPlaneTilt,
  PencilLine,
  Keyboard,
  Eraser,
  Signature,
} from "@phosphor-icons/react";
import ContentLoader from "../../components/shared/ContentLoader";
import "./QuestionnaireResponsePage.css";

// ---------------------------------------------------------------------------
// Patient signature (questionnaire.requireSignature)
// Inline block at the bottom of the fill stage. Drawn signatures are exported
// as PNG at a bounded width so the payload stays small (~20–60KB); typed
// signatures are plain text. Shape matches the forms flow: { data, type }.
// ---------------------------------------------------------------------------
const SIGNATURE_CANVAS_HEIGHT = 180;
const SIGNATURE_EXPORT_MAX_WIDTH = 640;
const SIGNATURE_TYPED_MAX = 120;

function QuestionnaireSignaturePad({ value, onChange }) {
  const [mode, setMode] = useState(value?.type === "typed" ? "typed" : "drawn");
  const [typed, setTyped] = useState(value?.type === "typed" ? value.data : "");
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    canvas.width = container.clientWidth;
    canvas.height = SIGNATURE_CANVAS_HEIGHT;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#1f2937";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  // (Re)initialize whenever the drawn tab is shown. Switching tabs discards
  // the drawing, so clear the parent value too.
  useEffect(() => {
    if (mode !== "drawn") return;
    initCanvas();
    setHasDrawn(false);
  }, [mode, initCanvas]);

  const exportDataUrl = () => {
    const source = canvasRef.current;
    if (!source) return null;
    if (source.width <= SIGNATURE_EXPORT_MAX_WIDTH) return source.toDataURL("image/png");
    const scale = SIGNATURE_EXPORT_MAX_WIDTH / source.width;
    const out = document.createElement("canvas");
    out.width = SIGNATURE_EXPORT_MAX_WIDTH;
    out.height = Math.round(source.height * scale);
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(source, 0, 0, out.width, out.height);
    return out.toDataURL("image/png");
  };

  const pointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawingRef.current = true;
    setHasDrawn(true);
  };

  const move = (e) => {
    if (!drawingRef.current || !canvasRef.current) return;
    if (e.cancelable) e.preventDefault(); // keep the page from scrolling under the finger
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointFromEvent(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  // Commit the drawing to the parent on every stroke end so the submit button
  // reflects the current state without an extra "confirm" tap.
  const stop = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const data = exportDataUrl();
    if (data) onChange({ data, type: "drawn" });
  };

  const clear = () => {
    initCanvas();
    setHasDrawn(false);
    onChange(null);
  };

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setTyped("");
    setHasDrawn(false);
    onChange(null);
  };

  const handleTyped = (e) => {
    const next = e.target.value.slice(0, SIGNATURE_TYPED_MAX);
    setTyped(next);
    onChange(next.trim() ? { data: next.trim(), type: "typed" } : null);
  };

  return (
    <div className="qsig-block">
      <div className="qsig-tabs" role="tablist" aria-label="אופן החתימה">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "drawn"}
          className={`qsig-tab${mode === "drawn" ? " active" : ""}`}
          onClick={() => switchMode("drawn")}
        >
          <PencilLine size={18} weight={mode === "drawn" ? "fill" : "regular"} />
          <span>חתימה ביד</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "typed"}
          className={`qsig-tab${mode === "typed" ? " active" : ""}`}
          onClick={() => switchMode("typed")}
        >
          <Keyboard size={18} weight={mode === "typed" ? "fill" : "regular"} />
          <span>הקלדת שם</span>
        </button>
      </div>

      {mode === "drawn" ? (
        <div className="qsig-canvas-wrap">
          <canvas
            ref={canvasRef}
            className="qsig-canvas"
            aria-label="משטח חתימה"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={stop}
            onMouseLeave={stop}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={stop}
            onTouchCancel={stop}
          />
          {!hasDrawn && (
            <span className="qsig-canvas-hint" aria-hidden="true">
              חתמו כאן עם האצבע או העכבר
            </span>
          )}
          <button
            type="button"
            className="qsig-clear-btn"
            onClick={clear}
            disabled={!hasDrawn}
          >
            <Eraser size={16} weight="regular" />
            <span>נקה</span>
          </button>
        </div>
      ) : (
        <div className="qsig-typed-wrap">
          <input
            type="text"
            className="qsig-typed-input"
            value={typed}
            onChange={handleTyped}
            placeholder="הקלד/י את שמך המלא"
            maxLength={SIGNATURE_TYPED_MAX}
            dir="rtl"
            autoComplete="name"
          />
          {typed.trim() && (
            <div className="qsig-typed-preview">"{typed.trim()}"</div>
          )}
        </div>
      )}
    </div>
  );
}

// Map Cloud Function error codes (HttpsError) to Hebrew. The server throws
// English messages; never surface those to patients.
const publicErrorToHebrew = (error, fallback) => {
  const code = String(error?.code || "").replace(/^functions\//, "");
  const msg = String(error?.message || "");
  if (code === "not-found") return "הקישור לא נמצא. ייתכן שנשלח קישור חדש.";
  if (code === "failed-precondition") {
    if (/already been submitted/i.test(msg)) return "השאלון כבר נשלח. תודה!";
    if (/verification required/i.test(msg)) return "נדרש אימות זהות מחדש. רענן/י את הדף ונסה/י שוב.";
    if (/signature required/i.test(msg)) return "יש לחתום בתחתית השאלון לפני השליחה.";
    return "השאלון אינו זמין עוד. פנה/י למטפל/ת לקבלת קישור חדש.";
  }
  if (code === "invalid-argument") {
    if (/signature/i.test(msg)) return "החתימה לא נשמרה כראוי. נקה/י וחתום/י שוב.";
    return "הקישור אינו תקין.";
  }
  if (code === "unavailable" || code === "deadline-exceeded" || /network/i.test(msg)) {
    return "בעיית תקשורת. בדוק/י את החיבור לאינטרנט ונסה/י שוב.";
  }
  return fallback;
};


// Stages of the questionnaire flow
const STAGES = {
  LOADING: "loading",
  VERIFY: "verify",
  FILL: "fill",
  SUBMITTING: "submitting",
  SUCCESS: "success",
  ALREADY_COMPLETED: "already_completed",
  ERROR: "error",
};

function normalizeQuestionnaireFieldType(type) {
  return String(type || "").trim().toLowerCase();
}

// section_header is a visual divider — no answer, no validation
function buildInitialFieldValues(fields) {
  const initialValues = {};
  (fields || []).forEach((field) => {
    const fieldType = normalizeQuestionnaireFieldType(field.type);
    if (fieldType === "section_header") return;
    initialValues[field.id] =
      fieldType === "checkbox"
        ? false
        : fieldType === "multi_select"
          ? []
          : "";
  });
  return initialValues;
}

function QuestionnaireResponsePage() {
  const { linkId } = useParams();
  useNoIndex();
  const [stage, setStage] = useState(STAGES.LOADING);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [verificationValue, setVerificationValue] = useState("");
  const [verificationType, setVerificationType] = useState("phone"); // "phone" or "id"
  const [verificationError, setVerificationError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  // Patient signature { data, type } — only collected when the link requires it
  const [signature, setSignature] = useState(null);

  // Load questionnaire data on mount
  useEffect(() => {
    const loadQuestionnaire = async () => {
      try {
        const data = await getQuestionnairePublic(linkId);

        if (!data.success) {
          setErrorMessage(data.message || "הקישור לא נמצא");
          setStage(STAGES.ERROR);
          return;
        }

        // Check if already completed
        if (data.status === "completed") {
          setQuestionnaireData(data);
          setStage(STAGES.ALREADY_COMPLETED);
          return;
        }

        setQuestionnaireData(data);

        // If already verified (status === "viewed"), skip verification
        if (data.verificationStatus === "verified") {
          setFieldValues(buildInitialFieldValues(data.fields));
          setStage(STAGES.FILL);
        } else {
          // Need verification - set default type based on whether patient has ID
          setVerificationType(data.hasIdNumber ? "id" : "phone");
          setStage(STAGES.VERIFY);
        }
      } catch (error) {
        console.error("[CK-7200] Error loading questionnaire:", {
          errorCode: "CK-7200",
          errorMessage: error.message,
          linkId,
          timestamp: new Date().toISOString(),
        });
        setErrorMessage(publicErrorToHebrew(error, "אירעה שגיאה בטעינת השאלון"));
        setStage(STAGES.ERROR);
      }
    };

    loadQuestionnaire();
  }, [linkId]);

  // Handle verification
  const handleVerify = async () => {
    if (verificationType === "phone" && verificationValue.length !== 4) {
      setVerificationError("יש להזין 4 ספרות אחרונות");
      return;
    }
    if (verificationType === "id" && verificationValue.length < 5) {
      setVerificationError("יש להזין תעודת זהות תקינה");
      return;
    }

    setVerificationError("");

    try {
      const result = await verifyQuestionnaireIdentity(
        linkId,
        verificationValue,
        verificationType
      );

      if (!result.verified) {
        setVerificationError(
          verificationType === "phone"
            ? "הקוד שהוזן שגוי"
            : "תעודת הזהות שהוזנה שגויה"
        );
        return;
      }

      setFieldValues(buildInitialFieldValues(questionnaireData.fields));
      setStage(STAGES.FILL);
    } catch (error) {
      console.error("[CK-7201] Verification error:", {
        errorCode: "CK-7201",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      setVerificationError(publicErrorToHebrew(error, "אירעה שגיאה באימות"));
    }
  };

  // Update field value
  const handleFieldChange = (fieldId, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  // multi_select stores an array of chosen options — toggle membership.
  const handleMultiSelectToggle = (fieldId, option) => {
    setFieldValues((prev) => {
      const current = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
      const value = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [fieldId]: value };
    });
  };

  const isCompleteIsoDate = (value) =>
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

  // Legacy links have no flag → no signature step
  const requireSignature = questionnaireData?.requireSignature === true;
  const hasSignature = !!(signature && signature.data && signature.type);

  // Validate required fields
  const validateFields = () => {
    for (const field of questionnaireData.fields || []) {
      const fieldType = normalizeQuestionnaireFieldType(field.type);
      if (fieldType === "section_header") continue;
      if (field.required && fieldType !== "checkbox") {
        const value = fieldValues[field.id];
        if (fieldType === "date") {
          if (!isCompleteIsoDate(value)) return false;
        } else if (fieldType === "multi_select") {
          if (!Array.isArray(value) || value.length === 0) return false;
        } else if (
          !value ||
          (typeof value === "string" && !value.trim())
        ) {
          return false;
        }
      }
    }
    return true;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }
    if (requireSignature && !hasSignature) {
      return;
    }

    setStage(STAGES.SUBMITTING);

    try {
      // Build fields array with values (section headers carry no answer)
      const fieldsWithValues = (questionnaireData.fields || []).map((field) => ({
        id: field.id,
        label: field.label,
        type: field.type,
        value: fieldValues[field.id] ?? null,
      }));

      // Get device info
      const deviceInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        timestamp: new Date().toISOString(),
      };

      const result = await submitQuestionnaireResponse(
        linkId,
        fieldsWithValues,
        deviceInfo,
        requireSignature ? signature : null
      );

      if (result.success) {
        setStage(STAGES.SUCCESS);
      } else {
        setErrorMessage(result.message || "אירעה שגיאה בשליחת השאלון");
        setStage(STAGES.ERROR);
      }
    } catch (error) {
      console.error("[CK-7202] Submit error:", {
        errorCode: "CK-7202",
        errorMessage: error.message,
        linkId,
        timestamp: new Date().toISOString(),
      });
      setErrorMessage(publicErrorToHebrew(error, "אירעה שגיאה בשליחת השאלון"));
      setStage(STAGES.ERROR);
    }
  };

  // Render loading state
  if (stage === STAGES.LOADING) {
    return (
      <div className="questionnaire-page">
        <div className="questionnaire-container loading-container">
          <ContentLoader fill message="טוען שאלון..." />
        </div>
      </div>
    );
  }

  const clinicHeader = (compact = false) => (
    <PublicClinicHeader
      clinic={questionnaireData?.clinic}
      eyebrow="שאלון למילוי"
      compact={compact}
    />
  );

  // Render error state
  if (stage === STAGES.ERROR) {
    return (
      <div className="questionnaire-page">
        <div className="questionnaire-container error-container">
          {clinicHeader(true)}
          <div className="q-status-circle q-status-warning">
            <Warning size={36} weight="fill" />
          </div>
          <h2>שגיאה</h2>
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Render already completed state
  if (stage === STAGES.ALREADY_COMPLETED) {
    return (
      <div className="questionnaire-page">
        <div className="questionnaire-container success-container">
          {clinicHeader(true)}
          <div className="q-status-circle q-status-success q-status-pop">
            <CheckCircle size={44} weight="fill" />
          </div>
          <h2>השאלון כבר מולא</h2>
          <p>
            השאלון "{questionnaireData.questionnaireName}" כבר מולא ונשלח בהצלחה.
          </p>
          <p className="success-hint">ניתן לסגור עמוד זה.</p>
        </div>
      </div>
    );
  }

  // Render success state
  if (stage === STAGES.SUCCESS) {
    return (
      <div className="questionnaire-page">
        <div className="questionnaire-container success-container">
          {clinicHeader(true)}
          <div className="q-status-circle q-status-success q-status-pop">
            <CheckCircle size={44} weight="fill" />
          </div>
          <h2>תודה רבה!</h2>
          <p>השאלון נשלח בהצלחה.</p>
          <p className="success-hint">ניתן לסגור עמוד זה.</p>
        </div>
      </div>
    );
  }

  // Render submitting state
  if (stage === STAGES.SUBMITTING) {
    return (
      <div className="questionnaire-page">
        <div className="questionnaire-container loading-container">
          <ContentLoader fill message="שולח את השאלון..." />
        </div>
      </div>
    );
  }

  // Render verification stage
  if (stage === STAGES.VERIFY) {
    return (
      <div className="questionnaire-page">
        <div className="questionnaire-container">
          {clinicHeader()}

          <div className="questionnaire-header">
            <p className="greeting">
              שלום <strong>{questionnaireData.patientFirstName}</strong>,
            </p>
            <div className="questionnaire-name">
              <ListBullets size={16} weight="fill" />
              <span>{questionnaireData.questionnaireName}</span>
            </div>
          </div>

          <div className="verification-section">
            <div className="q-status-circle q-status-primary">
              <ShieldCheck size={32} weight="fill" />
            </div>
            <h2>אימות זהות</h2>
            <p className="verification-description">
              לאבטחת המידע, יש לאמת את זהותך
            </p>

            {/* Verification type selector - only show if patient has ID */}
            {questionnaireData.hasIdNumber && (
              <div className="verification-type-selector">
                <button
                  type="button"
                  className={`type-btn ${verificationType === "phone" ? "active" : ""}`}
                  onClick={() => {
                    setVerificationType("phone");
                    setVerificationValue("");
                    setVerificationError("");
                  }}
                >
                  <Phone size={20} weight="regular" />
                  <span>טלפון</span>
                </button>
                <button
                  type="button"
                  className={`type-btn ${verificationType === "id" ? "active" : ""}`}
                  onClick={() => {
                    setVerificationType("id");
                    setVerificationValue("");
                    setVerificationError("");
                  }}
                >
                  <IdentificationCard size={20} weight="regular" />
                  <span>תעודת זהות</span>
                </button>
              </div>
            )}

            <div className="verification-input-group">
              <label>
                {verificationType === "phone"
                  ? "4 ספרות אחרונות של מספר הטלפון"
                  : "מספר תעודת זהות"}
              </label>
              <input
                type={verificationType === "phone" ? "tel" : "text"}
                value={verificationValue}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (verificationType === "phone") {
                    setVerificationValue(val.slice(0, 4));
                  } else {
                    setVerificationValue(val.slice(0, 9));
                  }
                }}
                placeholder={verificationType === "phone" ? "____" : "מספר תעודת זהות"}
                maxLength={verificationType === "phone" ? 4 : 9}
                dir="ltr"
                className={verificationError ? "error" : ""}
              />
              {verificationError && (
                <span className="error-message">{verificationError}</span>
              )}
            </div>

            <button
              className="verify-btn"
              onClick={handleVerify}
              disabled={
                verificationType === "phone"
                  ? verificationValue.length !== 4
                  : verificationValue.length < 5
              }
            >
              אמת והמשך
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render fill stage
  if (stage === STAGES.FILL) {
    const fieldsValid = validateFields();
    const isValid = fieldsValid && (!requireSignature || hasSignature);

    return (
      <div className="questionnaire-page">
        <div className="questionnaire-container fill-container">
          {clinicHeader(true)}

          <div className="questionnaire-header">
            <h1>{questionnaireData.questionnaireName}</h1>
            <p className="fill-instruction">
              {requireSignature
                ? "יש למלא את כל השדות, לחתום בתחתית ולשלוח"
                : "יש למלא את כל השדות ולשלוח"}
            </p>
          </div>

          <div className="questionnaire-fields">
            {(questionnaireData.fields || []).map((field) => {
              const fieldType = normalizeQuestionnaireFieldType(field.type);

              if (fieldType === "section_header") {
                return (
                  <div key={field.id} className="field-group section-header-group">
                    <h3 className="section-header-title">{field.label}</h3>
                  </div>
                );
              }

              return (
              <div key={field.id} className="field-group">
                <label className={field.required ? "required" : ""}>
                  {field.label}
                  {field.required && <span className="required-mark">*</span>}
                </label>

                {fieldType === "short_text" && (
                  <input
                    type="text"
                    value={fieldValues[field.id] || ""}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    placeholder="הקלד כאן..."
                    dir="rtl"
                  />
                )}

                {fieldType === "long_text" && (
                  <textarea
                    value={fieldValues[field.id] || ""}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    placeholder="הקלד כאן..."
                    rows={4}
                    dir="rtl"
                  />
                )}

                {fieldType === "date" && (
                  <DatePicker
                    value={fieldValues[field.id] || ""}
                    onChange={(e) =>
                      handleFieldChange(field.id, e.target.value)
                    }
                    className="questionnaire-date-picker"
                  />
                )}

                {fieldType === "checkbox" && (
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={fieldValues[field.id] || false}
                      onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">{field.label}</span>
                  </label>
                )}

                {fieldType === "select" && (
                  <select
                    className="questionnaire-select"
                    value={fieldValues[field.id] || ""}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    dir="rtl"
                  >
                    <option value="" disabled>
                      בחר תשובה...
                    </option>
                    {(field.options || []).map((option, optIdx) => (
                      <option key={optIdx} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}

                {fieldType === "radio" && (
                  <div className="radio-options" role="radiogroup">
                    {(field.options || []).map((option, optIdx) => (
                      <label key={optIdx} className="radio-label">
                        <input
                          type="radio"
                          name={`field-${field.id}`}
                          value={option}
                          checked={fieldValues[field.id] === option}
                          onChange={() => handleFieldChange(field.id, option)}
                        />
                        <span className="radio-custom"></span>
                        <span className="radio-text">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {fieldType === "multi_select" && (
                  <div className="radio-options">
                    {(field.options || []).map((option, optIdx) => (
                      <label key={optIdx} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={
                            Array.isArray(fieldValues[field.id]) &&
                            fieldValues[field.id].includes(option)
                          }
                          onChange={() =>
                            handleMultiSelectToggle(field.id, option)
                          }
                        />
                        <span className="checkbox-custom"></span>
                        <span className="checkbox-text">{option}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
            })}
          </div>

          {requireSignature && (
            <div className="field-group qsig-section">
              <div className="qsig-section-header">
                <div className="qsig-section-icon">
                  <Signature size={20} weight="fill" />
                </div>
                <div className="qsig-section-text">
                  <label className="required">
                    חתימת המטופל/ת
                    <span className="required-mark">*</span>
                  </label>
                  <p className="qsig-section-desc">
                    חתימתך מאשרת שהתשובות נכונות ומולאו על ידך. ניתן לחתום ביד או להקליד שם.
                  </p>
                </div>
              </div>
              <QuestionnaireSignaturePad value={signature} onChange={setSignature} />
              {fieldsValid && !hasSignature && (
                <p className="qsig-missing-hint">
                  <Warning size={14} weight="fill" />
                  <span>כדי לשלוח את השאלון יש לחתום</span>
                </p>
              )}
            </div>
          )}

          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={!isValid}
          >
            <PaperPlaneTilt size={20} weight="bold" />
            <span>{requireSignature ? "חתום/י ושלח/י שאלון" : "שלח שאלון"}</span>
          </button>

          <p className="no-save-hint">
            * לא ניתן לשמור טיוטה - יש למלא ולשלוח בפעם אחת
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default QuestionnaireResponsePage;
